import React from "react";
import { Routes, Route } from "react-router-dom";
import RoleGuard from "../common/RoleGuard";
import { useAuth } from "../../hooks/useAuth";

// Import our implemented components
import CourseCatalog from "../../pages/elearning/CourseCatalog";
import ModuleViewer from "../../pages/elearning/ModuleViewer";
import QuizInterface from "../../pages/elearning/QuizInterface";
import DiscussionBoard from "../../pages/elearning/DiscussionBoard";
import CourseDiscussionBoard from "../../pages/elearning/CourseDiscussionBoard";
import LiveSessionRoom from "../../pages/elearning/LiveSessionRoom";
import TeacherGradingDashboard from "../../pages/elearning/TeacherGradingDashboard";
import QuizGradingDashboard from "../../pages/elearning/QuizGradingDashboard";
import LearningAnalyticsDashboard from "../../pages/elearning/LearningAnalyticsDashboard";
import ContentManagement from "../../pages/elearning/ContentManagement";
import CourseContentManagement from "../../pages/elearning/CourseContentManagement";
import QuizManagement from "../../pages/elearning/QuizManagement";
import QuizEditor from "../../pages/elearning/QuizEditor";
import { ECourses } from "../../pages/elearning/ECourses";
import TeacherLiveSessions from "../../pages/teacher/TeacherLiveSessions";
import TeacherCourseView from "../../pages/elearning/TeacherCourseView";
import TeacherCourseEdit from "../../pages/elearning/TeacherCourseEdit";
import CourseDetail from "../../pages/student/CourseDetail";

// Enhanced Quiz Components
import { QuizDashboard, QuizTaking, QuizResults, QuizDetails } from "./quiz";
import QuizCreatorRoute from "./QuizCreatorRoute";

// Smart component that shows different views based on user role
const ELearningHome: React.FC = () => {
  const { user } = useAuth();

  // If user is a teacher or branch admin, show ECourses (their created courses)
  if (user?.roles.includes("teacher") || user?.roles.includes("branch_admin")) {
    return <ECourses />;
  }

  // Otherwise (students), show the course catalog
  return <CourseCatalog />;
};

const ELearningRouter: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Routes>
        {/* Smart home route - shows different views based on user role */}
        <Route path="/" element={<ELearningHome />} />
        <Route path="/courses" element={<ELearningHome />} />

        {/* Course catalog route specifically for students */}
        <Route
          path="/catalog"
          element={
            <RoleGuard allowedRoles={["student"]}>
              <CourseCatalog />
            </RoleGuard>
          }
        />

        {/* Course detail route for students */}
        <Route
          path="/course/:courseId"
          element={
            <RoleGuard allowedRoles={["student"]}>
              <CourseDetail />
            </RoleGuard>
          }
        />

        {/* Course specific routes */}
        <Route
          path="/courses/:courseId"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <TeacherCourseView />
            </RoleGuard>
          }
        />
        <Route
          path="/courses/:courseId/edit"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <TeacherCourseEdit />
            </RoleGuard>
          }
        />
        <Route
          path="/courses/:courseId/modules/:moduleId"
          element={<ModuleViewer />}
        />
        <Route
          path="/courses/:courseId/content"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <ContentManagement />
            </RoleGuard>
          }
        />
        <Route
          path="/courses/:courseId/manage"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <CourseContentManagement />
            </RoleGuard>
          }
        />
        <Route
          path="/courses/:courseId/discussions"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin", "student"]}>
              <CourseDiscussionBoard />
            </RoleGuard>
          }
        />
        <Route
          path="/discussions/:discussionId"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin", "student"]}>
              <DiscussionBoard />
            </RoleGuard>
          }
        />

        {/* Enhanced Quiz Routes for Students */}
        <Route
          path="/student/quizzes"
          element={
            <RoleGuard allowedRoles={["student"]}>
              <QuizDashboard userType="student" />
            </RoleGuard>
          }
        />
        <Route
          path="/student/quizzes/:quizId"
          element={
            <RoleGuard allowedRoles={["student"]}>
              <QuizDetails />
            </RoleGuard>
          }
        />
        <Route
          path="/student/quizzes/:quizId/take"
          element={
            <RoleGuard allowedRoles={["student"]}>
              <QuizTaking />
            </RoleGuard>
          }
        />
        <Route
          path="/student/quizzes/:quizId/results"
          element={
            <RoleGuard allowedRoles={["student"]}>
              <QuizResults />
            </RoleGuard>
          }
        />
        <Route
          path="/student/quiz-attempts/:attemptId/results"
          element={
            <RoleGuard allowedRoles={["student"]}>
              <QuizResults />
            </RoleGuard>
          }
        />

        {/* Legacy quiz route - redirect to enhanced interface */}
        <Route
          path="/quizzes/:quizId"
          element={
            <RoleGuard allowedRoles={["student"]}>
              <QuizTaking />
            </RoleGuard>
          }
        />

        {/* Enhanced Quiz Routes for Teachers */}
        <Route
          path="/teacher/quizzes"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <QuizDashboard userType="teacher" />
            </RoleGuard>
          }
        />
        <Route
          path="/teacher/quizzes/create"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <QuizCreatorRoute />
            </RoleGuard>
          }
        />
        <Route
          path="/teacher/quizzes/:quizId/edit"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <QuizCreatorRoute />
            </RoleGuard>
          }
        />
        <Route
          path="/teacher/quizzes/:quizId/preview"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <QuizTaking preview={true} />
            </RoleGuard>
          }
        />
        <Route
          path="/teacher/quizzes/:quizId/analytics"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <LearningAnalyticsDashboard />
            </RoleGuard>
          }
        />
        {/* Alias route: allow /quizzes/:quizId/analytics to mount the same analytics view
            so links that use the shorter path (e.g. /elearning/quizzes/:id/analytics)
            will render the analytics component and trigger its data fetch. */}
        <Route
          path="/quizzes/:quizId/analytics"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <LearningAnalyticsDashboard />
            </RoleGuard>
          }
        />
        <Route
          path="/quizzes/:quizId/grading"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <QuizGradingDashboard />
            </RoleGuard>
          }
        />

        {/* Teacher-specific routes */}
        <Route
          path="/my-courses"
          element={
            <RoleGuard allowedRoles={["student"]}>
              <CourseCatalog />
            </RoleGuard>
          }
        />
        <Route
          path="/quizzes/:quizId"
          element={
            <RoleGuard allowedRoles={["student"]}>
              <QuizInterface />
            </RoleGuard>
          }
        />
        <Route
          path="/live-sessions/:sessionId"
          element={
            <RoleGuard allowedRoles={["student", "teacher"]}>
              <LiveSessionRoom />
            </RoleGuard>
          }
        />

        {/* Teacher-specific routes */}
        <Route
          path="/my-ecourses"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <ECourses />
            </RoleGuard>
          }
        />
        <Route
          path="/quizzes"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <QuizManagement />
            </RoleGuard>
          }
        />
        <Route
          path="/quizzes/new"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <QuizEditor />
            </RoleGuard>
          }
        />
        <Route
          path="/quizzes/:quizId/edit"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <QuizEditor />
            </RoleGuard>
          }
        />
        <Route
          path="/dashboard"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <LearningAnalyticsDashboard />
            </RoleGuard>
          }
        />
        <Route
          path="/courses/:courseId/content"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <ContentManagement />
            </RoleGuard>
          }
        />
        <Route
          path="/grading"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <TeacherGradingDashboard />
            </RoleGuard>
          }
        />
        <Route
          path="/grading/:assignmentId"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <TeacherGradingDashboard />
            </RoleGuard>
          }
        />
        <Route
          path="/teacher/live-sessions"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <TeacherLiveSessions />
            </RoleGuard>
          }
        />
        <Route
          path="/analytics"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <LearningAnalyticsDashboard />
            </RoleGuard>
          }
        />
        <Route
          path="/sessions"
          element={
            <RoleGuard allowedRoles={["teacher", "branch_admin"]}>
              <LiveSessionRoom />
            </RoleGuard>
          }
        />

        {/* Branch Admin routes */}
        <Route
          path="/admin"
          element={
            <RoleGuard allowedRoles={["branch_admin"]}>
              <LearningAnalyticsDashboard />
            </RoleGuard>
          }
        />
        <Route
          path="/admin/content"
          element={
            <RoleGuard allowedRoles={["branch_admin"]}>
              <ContentManagement />
            </RoleGuard>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <RoleGuard allowedRoles={["branch_admin"]}>
              <LearningAnalyticsDashboard />
            </RoleGuard>
          }
        />
      </Routes>
    </div>
  );
};

export default ELearningRouter;
